// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { closeButtonProps } from "@bg/XDL/PreStyle/modal";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { color } from "@bg/XDL/Utilities";
import { mergeWithCss } from "@bg/XDL/Utilities/mergeWithCss";
import xScreen from "@bg/XDL/Utilities/xScreen";
import { associatedSalesRepModalStyles } from "@bgs/App/Components/Cart/AssociatedSalesRepModal";
import ModalPresentationPropsDefault from "@insite/mobius/Modal/presentationProps";
import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

associatedSalesRepModalStyles.modal = {
    size: 1129,
    mergeCss: true,
    closeButtonIconProps: {
        ...ModalPresentationPropsDefault?.closeButtonIconProps,
        size: 18,
    },
    closeButtonProps: {
        ...mergeWithCss(closeButtonProps, {
            css: css`
                ${closeButtonProps.css}
                top: 20px;
                right: 20px;
                ${xScreen("lte", "xs")} {
                    display: none;
                }
            `,
        }),
        size: 18,
    },
    cssOverrides: {
        headlineTypography: css`
            @media only screen and (max-width: 426px) {
                padding-top: 0;
            }
        `,
        modalTitle: css`
            padding: 20px;
            ${xScreen("lte", "sm")} {
                padding: 20px;
            }
            ${xScreen("lte", "xs")} {
                padding-bottom: 12px;
            }
        `,
        modalContent: css`
            padding: 0 20px 20px;
            ${xScreen("lte", "sm")} {
                padding: 0 20px 20px;
            }
        `,
    },
};

associatedSalesRepModalStyles.radioGroupStyles = {
    mergeCss: true,
    css: css`
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        ${xScreen("lte", "md")} {
            grid-template-columns: repeat(2, 1fr);
        }
        ${xScreen("lt", "sm")} {
            grid-template-columns: 1fr;
        }
        ${xScreen("lte", "xs")} {
            gap: 12px;
        }
        > ${TypographyStyle} {
            display: none;
        }

        ${RadioStyle} {
            margin-top: 0;
            & + ${RadioStyle} {
                margin-top: 0;
            }
            input[type="radio"] {
                display: none;
            }
            ${TypographyStyle} {
                width: 100%;
                margin-left: 0;
                &::before {
                    display: none;
                }
                > div {
                    width: 100%;
                }
            }
            input:checked + label::after {
                display: none;
            }
        }
    `,
};

associatedSalesRepModalStyles.salesRepContainer = {
    css: css`
        display: grid;
        grid-template-columns: 116px 1fr;
        column-gap: 12px;
        padding: 4px;
        ${({ selected }: any) =>
            selected &&
            css`
                border: 1px solid ${color("primary.main")};
            `}
        ${xScreen("lte", "xs")} {
            grid-template-columns: 88px 1fr;
        }
    `,
};

associatedSalesRepModalStyles.image = {
    width: "116px",
    height: "116px",
    css: css`
        ${xScreen("lte", "xs")} {
            width: 88px;
            height: 88px;
        }
        img {
            object-fit: cover;
        }
    `,
};

associatedSalesRepModalStyles.infoItem = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 4px;
        justify-content: center;
    `,
};

associatedSalesRepModalStyles.name = {
    css: css`
        display: flex;
        flex-direction: column;
        ${TypographyStyle} {
            font-size: ${fontConfig.fontSize.regular}px;
            line-height: 21px;
            font-weight: ${fontConfig.weight.bold};
        }
    `,
};

associatedSalesRepModalStyles.email = {
    size: 12,
    lineHeight: fontConfig.lineHeight.xSmall,
    weight: fontConfig.weight.semiBold,
    color: "text.main",
};

associatedSalesRepModalStyles.actionsContainer = {
    css: css`
        margin-top: 20px;
        border-top: 2px solid ${color("common.ui3")};
        padding-top: 20px;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        column-gap: 32px;
        flex-wrap: wrap;
        ${xScreen("lte", "xs")} {
            justify-content: center;
        }
    `,
};

associatedSalesRepModalStyles.skipLink = {
    typographyProps: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.regular,
    },
    css: css`
        ${TypographyStyle} {
            color: ${color("text.main")};
        }
        ${xScreen("lte", "xs")} {
            margin-bottom: 20px;
        }
    `,
};
